import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../../../Constant";
import { GlobalLoading, showLoading } from "react-global-loading";
import { shortMonthToFull, handleUserLevelAccessPermissions, getAllYears, validateRolesIIPManufacturing, shortMonthToMonthNumber } from "../../../../Custom/Custom";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import messages from "../../../../assets/Messages.json";
import titlesData from "../../../../titles.json";
import Select from "react-select";
import { useSessionUserFromMe } from "../../../../hooks/useSessionUserFromMe";

function Estimation() {
  const [itemList, setItemList] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonth] = useState([]);
  const [itemCode, setItemCode] = useState("");
  const [yearName, setYearName] = useState("");
  const [monthName, setMonthName] = useState("");
  const [allFormData, setAllFormData] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [
    monthlyResponseEstimationStatus,
    setMonthlyResponseEstimationStatus,
  ] = useState(null);
  const [buttonAccess, setButtonAccess] = useState({
    userLevel: "",
    roles: "",
    isVisible: false,
  });

  const meUnreachableWarnedRef = useRef(false);

  const {
    user: me,
    loading: authLoading,
    error: authErr,
    config,
    meUnreachable,
  } = useSessionUserFromMe();

  const navigate = useNavigate();

  const storedLanguage =
    localStorage.getItem("language");

  // ================= GET ITEM LIST =================

  async function getItems() {
    showLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}api/iip/IIPItemNameFetch`,
        config
      );

      console.log(
        "ITEM RESPONSE",
        response.data
      );

      if (response.data.status) {
        setItemList(
          response.data.data
            .IIPItemName || []
        );
      } else {
        setItemList([]);
      }
    } catch (error) {
      console.error(error);

      setItemList([]);
    } finally {
      showLoading(false);
    }
  }

  // ================= GET TABLE DATA =================

  const getIndustryMonthlyData =
    async (year, month, itemCode) => {
      if (
        !year ||
        !month ||
        !itemCode
      )
        return;

      showLoading(true);

      try {
        const payload = {
          year: year,
          month: month,
          itemCode: itemCode,
        };

        const response = await axios.post(
          `${API_URL}api/iip/IIPIndustryMonthlyDataFetch`,
          payload,
          config
        );

        console.log(
          "TABLE RESPONSE",
          response.data
        );

        if (response.data.status) {
          setAllFormData(
            response.data.data || []
          );
          setUnitList(
            response.data.getUnitCodesdata || []
          );
          setMonthlyResponseEstimationStatus(
            response.data.ESTIMATION_STATUS ??
              response.data.estimation_status ??
              null
          );
        } else {
          setAllFormData([]);
          setUnitList([]);
          setMonthlyResponseEstimationStatus(null);
          toast.error(response.data.message || "No data found", messages.COLOR_RED);
        }
      } catch (error) {
        console.error(error);

        setAllFormData([]);
        setUnitList([]);
        setMonthlyResponseEstimationStatus(null);
      } finally {
        showLoading(false);
      }
    };

  // ================= INITIAL LOAD (cookie session + GET /api/auth/me for user claims) =================

  useEffect(() => {
    if (authLoading) return;

    if (authErr || !me?.id) {
      toast.error(
        "Session invalid or expired. Please sign in again.",
        messages.COLOR_RED
      );
      navigate("/", { replace: true });
      return;
    }

    if (meUnreachable && !meUnreachableWarnedRef.current) {
      meUnreachableWarnedRef.current = true;
      // toast.warn(
      //   "Profile API (/api/auth/me) abhi respond nahi kar rahi — cached session se aage badh rahe hain.",
      //   messages.COLOR_RED
      // );
    }

    const level = handleUserLevelAccessPermissions(me);

    setButtonAccess(level);

    const getMonthsList = () => {
      showLoading(true);

      axios
        .get(
          `${API_URL}api/graph/getMonths/2019`,
          config
        )
        .then((res) => {
          if (res.data.status) {
            setMonth(res.data.data);
          }
        })
        .catch((err) =>
          console.error(err)
        )
        .finally(() =>
          showLoading(false)
        );
    };

    const userRole = async () => {
      showLoading(true);

      try {
        const role =
          await validateRolesIIPManufacturing(
            me
          );

        if (role === true) {
          setYears(getAllYears());
          getMonthsList();
          getItems();
        } else {
          alert(
            "You are not an authorised user to capture the data for IIP Estimation."
          );

          navigate("/dashboard");
        }
      } catch (error) {
        console.log(error);
      } finally {
        showLoading(false);
      }
    };

    userRole();
  }, [authLoading, authErr, me, navigate, config]);

  // ================= DROPDOWN CHANGE =================

  const handleDropdownChange = (
    e
  ) => {
    const { name, value } = e.target;

    switch (name) {
      case "month_name":
        setMonthName(value);

        setItemCode("");

        setAllFormData([]);
        setUnitList([]);
        setMonthlyResponseEstimationStatus(null);

        break;

      case "year_name":
        const today = new Date();

        const currentYear =
          today.getFullYear();

        const selectedYear =
          parseInt(value);

        if (
          selectedYear > currentYear
        ) {
          toast.error(
            "Invalid Year Selection.",
            messages.COLOR_RED
          );

          return;
        }

        setYearName(value);

        if (
          selectedYear ===
          currentYear &&
          monthName !== ""
        ) {
          const currentMonthNum =
            today.getMonth() + 1;

          const selectedMonthNum =
            parseInt(
              shortMonthToMonthNumber(
                monthName
              )
            );

          if (
            selectedMonthNum >
            currentMonthNum
          ) {
            setMonthName("");
          }
        }

        setItemCode("");

        setAllFormData([]);
        setUnitList([]);
        setMonthlyResponseEstimationStatus(null);

        break;

      case "item_name":
        setItemCode(value);

        getIndustryMonthlyData(
          yearName,
          monthName,
          value
        );

        break;

      default:
        break;
    }
  };

  // ================= RESET =================

  const resetFormData = () => {
    setMonthName("");

    setItemCode("");

    setYearName("");

    setAllFormData([]);
    setUnitList([]);
    setMonthlyResponseEstimationStatus(null);
  };

  // ================= VAlidation =================
  const handleDecimalInputChange = (
    e,
    index,
    fieldName
  ) => {
    const value = e.target.value;

    // Only numbers + decimal
    // Max 20 digits before decimal
    // Max 2 digits after decimal

    const regex =
      /^\d{0,20}(\.\d{0,2})?$/;

    if (
      value === "" ||
      regex.test(value)
    ) {
      const updatedData = [
        ...allFormData,
      ];

      updatedData[index][fieldName] =
        value;

      setAllFormData(updatedData);
    }
  };


  const itemOptions = itemList.map((ele) => ({
    value: ele.itemCode,
    label: `${ele.itemCode} - ${ele.itemName}`,
  }));

  const estimationActionsDisabled =
    String(
      monthlyResponseEstimationStatus ?? ""
    ).trim() === "11" ||
    allFormData.some(
      (item) =>
        String(
          item.ESTIMATION_STATUS ??
            item.estimation_status ??
            ""
        ).trim() === "11"
    );

  // ================= UPDATE DATA API =================

  const saveIndustryMonthlyData = async (
    action
  ) => {
    if (estimationActionsDisabled) {
      return;
    }

    if (allFormData.length === 0) {
      toast.error("No data available to update");
      return;
    }

    showLoading(true);

    try {

      // CREATE PAYLOAD

      const payload = {
        records: allFormData.map((item) => ({
          year: yearName,

          month: monthName,

          itemCode: itemCode,

          industryCode:
            item.INDUSTRY_CODE || "",

          districtCode:
            item.DISTRICT_CODE || "",

          itemPrice:
            item.ITEM_PRICE === ""
              ? null
              : Number(item.ITEM_PRICE),

          itemQuantity:
            item.ITEM_QUANTITY === ""
              ? null
              : Number(item.ITEM_QUANTITY),

          unitCode:
            item.UNIT_CODE || "",

          action,
        })),
      };

      console.log(
        "UPDATE PAYLOAD",
        payload
      );

      // API CALL

      const response = await axios.post(
        `${API_URL}api/iip/IIPIndustryMonthlyDataUpdate`,
        payload,
        config
      );

      console.log(
        "UPDATE RESPONSE",
        response.data
      );

      if (response.data.status) {

        toast.success(response.data.message || "Data Updated Successfully", messages.COLOR_RED);

        // REFRESH TABLE DATA

        getIndustryMonthlyData(
          yearName,
          monthName,
          itemCode
        );

      } else {

        toast.error(
          response.data.message ||
          "Failed to update data"
        );
      }

    } catch (error) {

      console.error(error);

      toast.error(
        "Something went wrong while updating data"
      );

    } finally {

      showLoading(false);

    }
  };

  return (
    <>
      <GlobalLoading />

      <ToastContainer />

      <div className="container-fluid py-3">

        {/* FILTER CARD */}

        <div className="card mx-3 mb-3">
          <div className="card-body pb-1">

            <div className="row align-items-center">

              <h4 className="page_heading">
                <div
                  dangerouslySetInnerHTML={{
                    __html: storedLanguage === "hi"
                      ? titlesData.hi.EstimationReport
                      : titlesData.en.Estimation_Report,
                  }}
                />
              </h4>

              <div className="row align-items-center">

                {/* YEAR */}

                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">

                  <label>
                    {storedLanguage ===
                      "hi"
                      ? titlesData.hi.YEAR
                      : titlesData.en.YEAR}
                  </label>

                  <select
                    name="year_name"
                    className="form-select"
                    onChange={
                      handleDropdownChange
                    }
                    value={yearName}
                  >
                    <option value="">
                      Select Year
                    </option>

                    {years &&
                      years.map(
                        (
                          ele,
                          index
                        ) => (
                          <option
                            key={index}
                            value={ele}
                          >
                            {ele}
                          </option>
                        )
                      )}
                  </select>
                </div>

                {/* MONTH */}

                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">

                  <label>
                    {storedLanguage ===
                      "hi"
                      ? titlesData.hi.MONTH
                      : titlesData.en.MONTH}
                  </label>

                  <select
                    name="month_name"
                    className="form-select"
                    onChange={
                      handleDropdownChange
                    }
                    value={monthName}
                  >
                    <option value="">
                      Select Month
                    </option>

                    {months &&
                      months
                        .filter(
                          (ele) => {
                            const currentYearStr =
                              new Date()
                                .getFullYear()
                                .toString();

                            if (
                              yearName ===
                              currentYearStr
                            ) {
                              const currentMonthNum =
                                new Date().getMonth() +
                                1;

                              const eleMonthNum =
                                parseInt(
                                  shortMonthToMonthNumber(
                                    ele.MONTH
                                  )
                                );

                              return (
                                eleMonthNum <=
                                currentMonthNum
                              );
                            }

                            return true;
                          }
                        )
                        .map(
                          (
                            ele,
                            i
                          ) => (
                            <option
                              key={i}
                              value={
                                ele.MONTH
                              }
                            >
                              {shortMonthToFull(
                                ele.MONTH
                              )}
                            </option>
                          )
                        )}
                  </select>
                </div>

                {/* ITEM */}

                {/* <div className="col-xl-4 col-lg-4 col-md-4 col-sm-6 col-12 mb-3">

                  <label>
                    Item
                  </label>

                  <select
                    name="item_name"
                    className="form-select"
                    onChange={
                      handleDropdownChange
                    }
                    value={itemCode}
                  >
                    <option value="">
                      Select Item
                    </option>

                    {itemList &&
                      itemList.map(
                        (ele, index) => (
                          <option
                            key={index}
                            value={ele.itemCode}
                          >
                            {ele.itemCode}{" "}
                            -{" "}
                            {ele.itemName}
                          </option>
                        )
                      )}
                  </select>
                </div> */}


                <div className="col-xl-4 col-lg-4 col-md-4 col-sm-6 col-12 mb-3">
                  <label>Item</label>
                  <Select
                    options={itemOptions}
                    value={
                      itemOptions.find(
                        (option) => option.value === itemCode
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      handleDropdownChange({
                        target: {
                          name: "item_name",
                          value: selectedOption
                            ? selectedOption.value
                            : "",
                        },
                      });
                    }}
                    placeholder="Select Item"
                    isSearchable={true}

                    // IMPORTANT FIX
                    menuPortalTarget={document.body}
                    menuPosition="fixed"

                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "100%",
                      }),

                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),

                      menu: (base) => ({
                        ...base,
                        width: "100%",
                        zIndex: 9999,
                      }),

                      menuList: (base) => ({
                        ...base,
                        maxHeight: "220px",
                      }),

                      option: (base, state) => ({
                        ...base,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",

                        backgroundColor: state.isFocused
                          ? "#fff3e6"
                          : "#fff",

                        color: "#000",
                        "&:active": {
                          backgroundColor: "#ffe0bf",
                        },
                      }),


                    }}
                  />

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE CARD */}

        {allFormData.length > 0 && (
          <div className="card mx-3 mb-3">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-striped align-middle">
                  <thead>
                    <tr>
                      <th>
                        S.No
                      </th>
                      <th>
                        District
                      </th>
                      <th>
                        Industry Name
                      </th>
                      <th>
                        Quantity
                      </th>
                      <th>
                        Unit
                      </th>
                      <th>
                        Price (₹) in Thousands
                      </th>
                      <th>
                        Industry Status (Open/Closed)
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {allFormData.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          {item.DISTRICT_NAME || "-"}
                        </td>
                        <td>
                          {item.INDUSTRIAL_UNIT_NAME || "-"}
                        </td>

                        {/* QUANTITY INPUT */}

                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={
                              item.ITEM_QUANTITY || ""
                            }
                            onChange={(e) =>
                              handleDecimalInputChange(
                                e,
                                index,
                                "ITEM_QUANTITY"
                              )
                            }
                          />
                        </td>

                        {/* UNIT */}

                        {/* <td>
                        <select
                          className="form-select"
                          value={
                            item.UNIT_NAME || ""
                          }
                          onChange={(e) => {
                            const updatedData = [
                              ...allFormData,
                            ];
                            updatedData[index].UNIT_NAME =
                              e.target.value;
                            setAllFormData(
                              updatedData
                            );
                          }}
                        >
                          <option value="">
                            Select Unit
                          </option>

                          {unitList &&
                            unitList.map(
                              (unit, unitIndex) => (
                                <option
                                  key={unitIndex}
                                  value={
                                    unit.unitName
                                  }
                                >
                                  {unit.unitName}
                                </option>
                              )
                            )}
                        </select>
                      </td> */}


                        <td>
                          <select
                            className="form-select"
                            // value={
                            //   item.UNIT_NAME || ""
                            // }
                            value={item.UNIT_CODE || ""}
                            onChange={(e) => {
                              const updatedData = [
                                ...allFormData,
                              ];
                              const selectedUnit =
                                unitList.find(
                                  (unit) =>
                                    String(unit.unitCode) ===
                                    String(e.target.value)
                                );

                              updatedData[index].UNIT_CODE =
                                e.target.value;

                              updatedData[index].UNIT_NAME =
                                selectedUnit?.unitName || "";

                              setAllFormData(updatedData);
                            }}
                          >
                            <option value="">
                              Select Unit
                            </option>

                            {unitList.map((unit, unitIndex) => (
                              <option
                                key={unitIndex}
                                value={unit.unitCode}
                              >
                                {unit.unitName}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* PRICE INPUT */}

                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={
                              item.ITEM_PRICE || ""
                            }
                            onChange={(e) =>
                              handleDecimalInputChange(
                                e,
                                index,
                                "ITEM_PRICE"
                              )
                            }
                          />
                        </td>

                        <td className="text-center">
                          {(() => {
                            const raw =
                              item.form_status ??
                              item.FORM_STATUS;
                            const v = String(
                              raw ?? ""
                            ).trim();
                            if (v === "1") {
                              return (
                                <span
                                  style={{
                                    color: "#198754",
                                    fontWeight: 600,
                                  }}
                                >
                                  Open
                                </span>
                              );
                            }
                            if (v === "0") {
                              return (
                                <span
                                  style={{
                                    color: "#dc3545",
                                    fontWeight: 600,
                                  }}
                                >
                                  Closed
                                </span>
                              );
                            }
                            return (
                              <span className="text-muted">
                                -
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-center mt-3">

                <button
                  type="button"
                  className="btn btn_orange mx-1"
                  disabled={estimationActionsDisabled}
                  onClick={() =>
                    saveIndustryMonthlyData("SAVE")
                  }
                >
                  Save
                </button>

                <button
                  type="button"
                  className="btn btn_orange mx-1"
                  disabled={estimationActionsDisabled}
                  onClick={() =>
                    saveIndustryMonthlyData("ACCEPT")
                  }
                >
                  Accept
                </button>

                <button
                  type="button"
                  className="btn btn_orange mx-1"
                  disabled={estimationActionsDisabled}
                  onClick={
                    resetFormData
                  }
                >
                  Reset
                </button>

                <button
                  type="button"
                  className="btn btn_orange mx-1"
                  onClick={() =>
                    navigate(
                      "/dashboard"
                    )
                  }
                >
                  Cancel
                </button>
              </div>


            </div>
          </div>

        )}
      </div>
    </>
  );
}

export default Estimation;